import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import Login from './Login';
import axios from 'axios';
import logo from './logo.svg';
import { fetchMachineVersionFromS3 } from './machineVersions';



interface ProductionEvent {
  machineId: string;
  eventType: string;
  measureValue: string;
  timestamp: string;
  processedAt: string;
}

interface DashboardData {
  events: ProductionEvent[];
  totalEvents: number;
  lastUpdated: string;
  errors?: any[];
  machineErrors?: Record<string, any>;
  machineStatus?: any[]; // Add this for last log data
  lastLogData?: Record<string, string | null>; // Add this for simple last log data
}

interface MachineStats {
  today: number;
  thisWeek: number;
  selectedMonth: number;
  total: number; // This will now represent customer-only cycles (since Live Since date)
  lifetimeTotal: number; // NEW: All cycles including pre-live
  lastActivity: string | null;
  errorInfo: {
    lastErrorTime: string | null;
    errorCount: number;
    warningCount: number;
    status: 'OK' | 'WARNING' | 'ERROR';
    recentErrors: Array<{
      id: string;
      time: string;
      errorId: string;
      severity: string;
      sensor: string;
      cause: string;
      machineStatus: string;
      resolved: boolean;
    }>;
  };
}

interface MachineConfig {
  location: string; // This will be treated as customer data
  city: string; // New field for city
  hw_id: string;
  subscription_size: string;
  service_package:string;
  live_since: string;
  typ: string; // Kauf/Leasing or PPU or Pilot
  ip_address: string;
  ip_office: string;
  sim_card: string;
  data_active: string;
  data_active_until: string;
  software_version: string;
  comments: string;
  last_updated: string | null;
  counters?: {
    today: number;
    this_week: number;
    current_month: number;
    customer_total: number;
    lifetime_total: number;
    last_activity: string | null;
    last_counter_update: string | null;
    last_processed_timestamp: string;
    [key: string]: any; // For monthly counters like month_2024_10
  };
}

interface ConfigData {
  configs: Record<string, MachineConfig>;
  totalMachines: number;
  lastUpdated: string;
}


const Dashboard: React.FC = () => {
  const { currentUser, logout, isAdmin } = useAuth();
  const [data] = useState<DashboardData | null>(null);
  const [configData, setConfigData] = useState<ConfigData | null>(null);
  const [machineStats, setMachineStats] = useState<Record<string, MachineStats>>({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [lastLogTimestamps, setLastLogTimestamps] = useState<Record<string, string | null>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedErrors, setResolvedErrors] = useState<Record<string, Array<{
    id: string, 
    originalErrorTimestamp: string, 
    errorId: string,
    severity: string,
    cause: string,
    sensor: string,
    originalStatus: string,
    resolvedBy: string, 
    resolvedAt: string,
    currentStatus: string
  }>>>({});
  const [resolvedMachines, setResolvedMachines] = useState<Set<string>>(new Set());
  const [issueTracking, setIssueTracking] = useState<Record<string, Array<{
    id: string,
    date: string,
    errorId: string,
    severity: string,
    cause: string,
    sensor: string,
    resolvedDate: string,
    resolvedBy: string,
    protocolLink: string
  }>>>({});
  const [editingIssue, setEditingIssue] = useState<string | null>(null);

  const updateIssue = (machineId: string, issueId: string, field: string, value: string) => {
    setIssueTracking(prev => ({
      ...prev,
      [machineId]: (prev[machineId] || []).map(issue => 
        issue.id === issueId ? { ...issue, [field]: value } : issue
      )
    }));
  };

  const deleteIssue = (machineId: string, issueId: string) => {
    setIssueTracking(prev => ({
      ...prev,
      [machineId]: (prev[machineId] || []).filter(issue => issue.id !== issueId)
    }));
  };

  const EditableIssueCell = ({ issue, field, machineId, isEditable = false }: {
    issue: any;
    field: string;
    machineId: string;
    isEditable?: boolean;
  }) => {
    const cellKey = `${issue.id}-${field}`;
    const isEditing = editingIssue === cellKey;
    const value = issue[field] || '';

    if (!isEditable || !isAdmin) {
      return <span>{value || '-'}</span>;
    }

    if (isEditing) {
      if (field === 'severity') {
        return (
          <select
            value={value}
            onChange={(e) => updateIssue(machineId, issue.id, field, e.target.value)}
            onBlur={() => setEditingIssue(null)}
            autoFocus
            style={{
              width: '100%',
              border: '1px solid #007aff',
              borderRadius: '3px',
              padding: '2px',
              fontSize: '12px'
            }}
          >
            <option value="LOW">LOW</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="HIGH">HIGH</option>
            <option value="CRITICAL">CRITICAL</option>
          </select>
        );
      } else {
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateIssue(machineId, issue.id, field, e.target.value)}
            onBlur={() => setEditingIssue(null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setEditingIssue(null);
              if (e.key === 'Escape') setEditingIssue(null);
            }}
            autoFocus
            style={{
              width: '100%',
              border: '1px solid #007aff',
              borderRadius: '3px',
              padding: '2px',
              fontSize: '12px'
            }}
          />
        );
      }
    }

    return (
      <span
        onClick={() => setEditingIssue(cellKey)}
        style={{
          cursor: 'pointer',
          display: 'block',
          minHeight: '16px',
          padding: '2px',
          backgroundColor: !value ? '#f8f9fa' : 'transparent'
        }}
        title="Click to edit"
      >
        {value || '(click to edit)'}
      </span>
    );
  };

  const CONFIG_API_URL = process.env.REACT_APP_CONFIG_API_URL || '';
  const LAST_LOG_API_URL = process.env.REACT_APP_LAST_LOG_API_URL || '';
  const COUNTER_UPDATER_API_URL = process.env.REACT_APP_COUNTER_UPDATER_API_URL || '';
  const MACHINE_ERRORS_API_URL = process.env.REACT_APP_MACHINE_ERRORS_API_URL || '';
  const allMachineIds = Array.from({ length: 50 }, (_, i) => `E${i + 1}`);

  // ... (keep all your existing date calculation functions) ...
  const getStartOfWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const startOfWeek = new Date(now.setDate(diff));
    startOfWeek.setHours(0, 0, 0, 0);
    return startOfWeek;
  };

  const getEndOfWeek = () => {
    const startOfWeek = getStartOfWeek();
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    return endOfWeek;
  };

  const getSelectedMonthRange = () => {
    const start = new Date(selectedYear, selectedMonth, 1);
    const end = new Date(selectedYear, selectedMonth + 1, 0);
    return { start, end };
  };

  const getWeekRange = () => {
    const start = getStartOfWeek();
    const end = getEndOfWeek();
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };


  const getTotalCyclesThisMonth = () => {
    return Object.entries(machineStats)
      .filter(([machineId, stats]) => {
        const config = configData?.configs[machineId];
        // Exclude E6 and machines with "Oater Office" location
        // return machineId !== 'E6' && 
        //       config?.location !== '[TEST] Oater-Office' &&
        //       config?.location !== '[READY] Oater-Office';
          return getMachineStatus(config?.location || '') === 'LIVE';
      })
      .reduce((total, [machineId, stats]) => total + stats.selectedMonth, 0);
  };

  const getMachineStatus = (location: string) => {
    const lower = location.toLowerCase();
    if (lower.startsWith('[live]')) return 'LIVE';
    if (lower.startsWith('[ready]')) return 'READY';
    if (lower.startsWith('[test]')) return 'TEST';
    return 'INACTIVE';
  };

  // Get counts for each status
  const getActiveStatusCounts = () => {
    if (!configData?.configs) return { live: 0, ready: 0, test: 0, total: 0 };
    
    const configs = Object.values(configData.configs);
    const live = configs.filter(config => getMachineStatus(config.location) === 'LIVE').length;
    const ready = configs.filter(config => getMachineStatus(config.location) === 'READY').length;
    const test = configs.filter(config => getMachineStatus(config.location) === 'TEST').length;
    
    return {
      live,
      ready,
      test,
      total: live + ready + test
    };
  };

  // Get selected month counter from historical data
  const getSelectedMonthCount = useCallback((config: MachineConfig) => {
    if (!config.counters) return 0;
    
    // For current month, use current_month counter
    const currentDate = new Date();
    if (selectedYear === currentDate.getFullYear() && selectedMonth === currentDate.getMonth()) {
      return config.counters.current_month || 0;
    }
    
    // For historical months, use stored monthly counter
    const monthKey = `month_${selectedYear}_${selectedMonth + 1}`;
    return config.counters[monthKey] || 0;
  }, [selectedYear, selectedMonth]);

  const getAvailableMonths = () => {
    if (!configData?.configs) return [];
    
    const months = new Set<string>();
    
    // Add current month
    const currentDate = new Date();
    months.add(`${currentDate.getFullYear()}-${currentDate.getMonth()}`);
    
    // Add historical months from counter data
    Object.values(configData.configs).forEach(config => {
      if (config.counters) {
        Object.keys(config.counters).forEach(key => {
          if (key.startsWith('month_')) {
            const [, year, month] = key.split('_');
            months.add(`${year}-${parseInt(month) - 1}`);
          }
        });
      }
    });
    
    return Array.from(months)
      .map(key => {
        const [year, month] = key.split('-');
        return {
          year: parseInt(year),
          month: parseInt(month),
          label: new Date(parseInt(year), parseInt(month)).toLocaleDateString('en-US', { 
            month: 'long', 
            year: 'numeric' 
          })
        };
      })
      .sort((a, b) => b.year - a.year || b.month - a.month);
  };

  const calculateProductionStats = useCallback((events: ProductionEvent[], machineErrors: Record<string, any> = {}): Record<string, MachineStats> => {
    const stats: Record<string, MachineStats> = {};
    const startOfWeek = getStartOfWeek();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    getSelectedMonthRange();
    
    // Create mapping from SW ID to HW ID
    const swIdToHwId: Record<string, string> = {};
    if (configData) {
      Object.entries(configData.configs).forEach(([hwId, config]) => {
        if (config.hw_id && config.hw_id !== 'Not Set') {
          swIdToHwId[config.hw_id] = hwId; // map SW ID (config.hw_id) to HW ID (hwId)
        }
      });
    }
    
    
    // Initialize stats for all HW IDs
    allMachineIds.forEach(machineId => {
      stats[machineId] = { 
      today: 0, 
      thisWeek: 0, 
      selectedMonth: 0, 
      total: 0, 
      lifetimeTotal: 0,
      lastActivity: null,
      errorInfo: {
        lastErrorTime: null,
        errorCount: 0,
        warningCount: 0,
        status: 'OK',
        recentErrors: []
        }
      };
    });

    const productionFinishedEvents = events.filter(event => 
      event.eventType === 'productionFinished' && event.measureValue === 'P99'
    );
    

    // Pre-process Live Since dates for all machines (for performance)
    const machineLiveSinceDates: Record<string, Date | null> = {};
    allMachineIds.forEach(machineId => {
      const config = configData?.configs[machineId];
      const liveSinceStr = config?.live_since;
      let liveSinceDate = null;
      
      if (liveSinceStr && liveSinceStr !== 'Not Set' && liveSinceStr.trim() !== '') {
        try {
          let dateToProcess = liveSinceStr.trim();
          
          // Handle DD.MM.YYYY format
          if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateToProcess)) {
            const [day, month, year] = dateToProcess.split('.');
            dateToProcess = `${year}-${month}-${day}`;
          }
          
          liveSinceDate = new Date(dateToProcess);
          
          if (isNaN(liveSinceDate.getTime())) {
            liveSinceDate = null;
          } else {
            liveSinceDate.setHours(0, 0, 0, 0);
          }
        } catch (e) {
          console.warn(`Error parsing Live Since date for ${machineId}: "${liveSinceStr}"`, e);
          liveSinceDate = null;
        }
      }
      
      machineLiveSinceDates[machineId] = liveSinceDate;
      
      // Debug for E28
      if (machineId === 'E28') {
      }
    });


    productionFinishedEvents.forEach(event => {
      const eventDate = new Date(event.timestamp);
      const swId = event.machineId; // This is actually the SW ID from the event
      const hwId = swIdToHwId[swId]; // Map to HW ID for stats storage
      
      if (!hwId) {
        console.warn(`⚠️ No HW ID mapping found for SW ID: ${swId}`);
        return; // Skip events from unmapped machines
      }
      
      if (!stats[hwId]) {
        stats[hwId] = { 
        today: 0, 
        thisWeek: 0, 
        selectedMonth: 0, 
        total: 0, 
        lifetimeTotal: 0,
        lastActivity: null,
        errorInfo: {
          lastErrorTime: null,
          errorCount: 0,
          warningCount: 0,
          status: 'OK',
          recentErrors: [],
        }
      };
      }
      
      const liveSinceDate = machineLiveSinceDates[hwId]; // Use HW ID for config lookup
      
      // Always count lifetime total (for expanded section)
      stats[hwId].lifetimeTotal++;
      
      // Update last activity time if this event is more recent
      if (!stats[hwId].lastActivity || eventDate > new Date(stats[hwId].lastActivity!)) {
        stats[hwId].lastActivity = event.timestamp;
      }
      
      // For main table counts (Today/Week/Month/Total), only count events AFTER Live Since date
      const shouldCountForCustomer = !liveSinceDate || eventDate >= liveSinceDate;
      
      if (shouldCountForCustomer) {
        // Count for customer-only total
        stats[hwId].total++;
        
        if (eventDate >= startOfToday) {
          stats[hwId].today++;
        }
        
        if (eventDate >= startOfWeek) {
          stats[hwId].thisWeek++;
        }
        
        // Skip month calculation here - use stored counters instead
      }
      });

    // Merge error information
    allMachineIds.forEach(machineId => {
      if (machineErrors[machineId]) {
        stats[machineId].errorInfo = machineErrors[machineId];
      }
    });

    // Debug E8: Final stats

    return stats;
  }, [selectedMonth, selectedYear, configData, getSelectedMonthCount]);

  const toggleRowExpansion = (machineId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(machineId)) {
      newExpandedRows.delete(machineId);
    } else {
      newExpandedRows.add(machineId);
    }
    setExpandedRows(newExpandedRows);
  };

  
  // 4. Create new function for live cycles total
  const getTotalLiveCyclesThisMonth = () => {
    if (!configData?.configs) return 0;
    
    return Object.entries(configData.configs)
      .filter(([machineId, config]) => {
        return getMachineStatus(config.location || '') === 'LIVE';
      })
      .reduce((total, [machineId, config]) => {
        return total + getSelectedMonthCount(config);
      }, 0);
  };




  const ExpandedMachineDetails = ({ errorInfo, machineId, config, stats }: { 
    errorInfo: MachineStats['errorInfo']; 
    machineId: string;
    config: MachineConfig;
    stats: MachineStats;
  }) => {
    // Add safety check for errorInfo
    const safeErrorInfo = errorInfo || {
      lastErrorTime: null,
      errorCount: 0,
      warningCount: 0,
      status: 'OK' as const,
      recentErrors: []
    };

    const formatTime = (timeString: string | null) => {
      if (!timeString) return 'Never';
      return new Date(timeString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const resolveAllWarnings = async () => {
      
      // Create resolved error record with full error details
      const resolvedError = {
        id: `${machineId}-${Date.now()}`,
        originalErrorTimestamp: safeErrorInfo.lastErrorTime || new Date().toISOString(),
        errorId: safeErrorInfo.recentErrors?.[0]?.errorId || 'UNKNOWN',
        severity: safeErrorInfo.recentErrors?.[0]?.severity || 'UNKNOWN',
        cause: safeErrorInfo.recentErrors?.[0]?.cause || 'No details available',
        sensor: safeErrorInfo.recentErrors?.[0]?.sensor || 'N/A',
        originalStatus: safeErrorInfo.status,
        resolvedBy: currentUser?.email || 'Unknown User',
        resolvedAt: new Date().toISOString(),
        currentStatus: 'OK'
      };
      
      // Update resolved errors state
      setResolvedErrors(prev => ({
        ...prev,
        [machineId]: [...(prev[machineId] || []), resolvedError]
      }));
      
      // Mark machine as resolved to clear error status
      setResolvedMachines(prev => {
        const newSet = new Set(prev);
        newSet.add(machineId);
        return newSet;
      });
      
      // For now, just show success message (until backend API is implemented)
      // Success notification - replace with toast in production
    };

    // Check if machine has been resolved
    const isResolved = resolvedMachines.has(machineId);
    const hasUnresolvedWarnings = !isResolved && (safeErrorInfo.status === 'WARNING' || safeErrorInfo.status === 'ERROR');

    return (
      <tr className="expanded-details-row">
        <td colSpan={14} style={{ padding: '0', border: 'none' }}>
          <div style={{ 
            backgroundColor: 'white',
            marginLeft: '60px', // Further reduced indentation to 60px
            width: '900px', // Much wider for the detailed table
            padding: '12px 16px',
            borderRadius: '6px',
            fontSize: '12px',
            display: 'flex',
            gap: '16px'
          }}>
            {/* Vertical field layout - each field as a row */}
            <div style={{ 
              backgroundColor: '#f8f9fa',
              padding: '12px',
              borderRadius: '4px',
              border: '1px solid #dee2e6',
              width: '350px'
            }}>
              
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e9ecef' }}>
                <span style={{ fontWeight: '600', color: '#495057', width: '120px' }}>IP Office:</span>
                <div style={{ width: '200px', textAlign: 'left' }}>
                  <EditableConfigCell machineId={machineId} field="ip_office" value={config.ip_office} />
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e9ecef' }}>
                <span style={{ fontWeight: '600', color: '#495057', width: '120px' }}>IP Customer:</span>
                <div style={{ width: '200px', textAlign: 'left' }}>
                  <EditableConfigCell machineId={machineId} field="ip_address" value={config.ip_address} />
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e9ecef' }}>
                <span style={{ fontWeight: '600', color: '#495057', width: '120px' }}>Software Version:</span>
                <div style={{ width: '200px', textAlign: 'left' }}>
                  <EditableConfigCell machineId={machineId} field="software_version" value={config.software_version} />
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e9ecef' }}>
                <span style={{ fontWeight: '600', color: '#495057', width: '120px' }}>Data Active:</span>
                <div style={{ width: '200px', textAlign: 'left' }}>
                  <EditableConfigCell machineId={machineId} field="data_active" value={config.data_active} />
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e9ecef' }}>
                <span style={{ fontWeight: '600', color: '#495057', width: '120px' }}>Active Until:</span>
                <div style={{ width: '200px', textAlign: 'left' }}>
                  <EditableConfigCell machineId={machineId} field="data_active_until" value={config.data_active_until} />
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e9ecef' }}>
                <span style={{ fontWeight: '600', color: '#495057', width: '120px' }}>SIM Card:</span>
                <div style={{ width: '200px', textAlign: 'left' }}>
                  <EditableConfigCell machineId={machineId} field="sim_card" value={config.sim_card} />
                </div>
              </div>
              

              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', backgroundColor: '#fff8dc' }}>
                <span style={{ fontWeight: '700', color: '#856404', width: '120px' }}>Lifetime Total:</span>
                <div style={{ width: '200px', textAlign: 'left', fontWeight: '600', color: '#856404' }}>
                  {stats.lifetimeTotal} cycles
                </div>
              </div>
            </div>
            
            {/* Status Field - Always show current status */}
            <div style={{ 
              backgroundColor: isResolved ? '#d4edda' : (hasUnresolvedWarnings ? '#fff3cd' : '#d4edda'),
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              width: '200px',
              marginBottom: '12px'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Status: </strong>
                <span style={{ 
                  color: isResolved ? '#28a745' : (safeErrorInfo.status === 'ERROR' ? '#dc3545' : safeErrorInfo.status === 'WARNING' ? '#ffc107' : '#28a745')
                }}>
                  {isResolved ? 'OK' : safeErrorInfo.status}
                </span>
              </div>
              
              {/* Show current error details if active */}
              {hasUnresolvedWarnings && safeErrorInfo.lastErrorTime && (
                <div style={{ marginBottom: '8px', fontSize: '10px' }}>
                  <strong>Last Error:</strong><br />
                  {formatTime(safeErrorInfo.lastErrorTime)}
                  
                  {safeErrorInfo.recentErrors?.length > 0 && (
                    <div style={{ marginTop: '4px', padding: '4px', backgroundColor: '#f8f9fa', borderRadius: '3px' }}>
                      <div style={{ fontSize: '9px', color: '#666' }}>
                        <strong>Error ID:</strong> {safeErrorInfo.recentErrors[0]?.errorId || 'N/A'}<br />
                        <strong>Severity:</strong> {safeErrorInfo.recentErrors[0]?.severity || 'N/A'}<br />
                        <strong>Cause:</strong> {safeErrorInfo.recentErrors[0]?.cause ? 
                          (safeErrorInfo.recentErrors[0].cause.length > 40 ? 
                            safeErrorInfo.recentErrors[0].cause.substring(0, 40) + '...' : 
                            safeErrorInfo.recentErrors[0].cause
                          ) : 'N/A'}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {hasUnresolvedWarnings && (
                <button
                  onClick={resolveAllWarnings}
                  style={{
                    padding: '6px 12px',
                    fontSize: '10px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  Mark as Resolved
                </button>
              )}
            </div>

            {/* Issue Tracking Table - Always visible */}
            <div style={{ 
              backgroundColor: '#f8f9fa',
              padding: '16px',
              borderRadius: '4px',
              width: '100%',
              marginTop: '8px'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '8px' 
              }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#30313d' }}>
                  Issue Tracking
                </div>
                {isAdmin && (
                  <button
                    onClick={() => {
                      const newId = `manual-${Date.now()}`;
                      const newIssue = {
                        id: newId,
                        date: new Date().toISOString(),
                        errorId: '',
                        severity: 'MEDIUM',
                        cause: '',
                        sensor: '',
                        resolvedDate: '',
                        resolvedBy: '',
                        protocolLink: `${process.env.REACT_APP_SHAREPOINT_BASE_URL}/${machineId}/${newId}`
                      };
                      
                      setIssueTracking(prev => ({
                        ...prev,
                        [machineId]: [...(prev[machineId] || []), newIssue]
                      }));
                      
                      setEditingIssue(`${newId}-errorId`);
                    }}
                    style={{
                      padding: '4px 12px',
                      fontSize: '11px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                  >
                    + Add Row
                  </button>
                )}
              </div>
              <div className="issues-card">
                <table className="issues-table">
                  <tbody>
                    <tr className="header-row">
                      <th>Date</th>
                      <th>ID</th>
                      <th>Severity</th>
                      <th>Cause</th>
                      <th>Sensor</th>
                      <th>Resolved</th>
                      <th>By</th>
                      <th>Protocol</th>
                      {isAdmin && <th>Del</th>}
                    </tr>
                    {(() => {
                      // Combine resolved errors and manually tracked issues
                      const allIssues = [
                        ...(resolvedErrors[machineId] || []).map(r => ({
                          id: r.id,
                          date: r.originalErrorTimestamp,
                          errorId: r.errorId,
                          severity: r.severity,
                          cause: r.cause,
                          sensor: r.sensor,
                          resolvedDate: r.resolvedAt,
                          resolvedBy: r.resolvedBy,
                          protocolLink: `${process.env.REACT_APP_SHAREPOINT_BASE_URL}/${machineId}/${r.id}`
                        })),
                        ...(issueTracking[machineId] || [])
                      ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 10);

                      if (allIssues.length === 0) {
                        return (
                          <tr style={{ backgroundColor: 'white', height: '32px' }}>
                            <td colSpan={isAdmin ? 9 : 8} style={{ 
                              padding: '8px',
                              borderBottom: '1px solid #dee2e6', 
                              fontSize: '12px',
                              textAlign: 'center',
                              color: '#6c757d',
                              fontStyle: 'italic'
                            }}>
                              No issues tracked yet
                            </td>
                          </tr>
                        );
                      }

                      return allIssues.map((issue, index) => {
                        const isManualIssue = issue.id.startsWith('manual-');
                        
                        return (
                          <tr key={issue.id} style={{ 
                            backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                            height: '32px'
                          }}>
                            <td style={{ 
                              padding: '8px',
                              borderBottom: '1px solid #dee2e6', 
                              fontSize: '12px',
                              verticalAlign: 'middle'
                            }}>
                              {formatTime(issue.date)}
                            </td>
                            <td style={{ 
                              padding: '8px', 
                              borderBottom: '1px solid #dee2e6', 
                              fontWeight: 'bold', 
                              fontSize: '12px', 
                              verticalAlign: 'middle'
                            }}>
                              <EditableIssueCell 
                                issue={issue} 
                                field="errorId" 
                                machineId={machineId} 
                                isEditable={isManualIssue}
                              />
                            </td>
                            <td style={{ 
                              padding: '8px', 
                              borderBottom: '1px solid #dee2e6',
                              fontWeight: 'bold',
                              fontSize: '12px',
                              verticalAlign: 'middle',
                              color: issue.severity === 'HIGH' || issue.severity === 'CRITICAL' ? '#dc3545' : '#ffc107'
                            }}>
                              <EditableIssueCell 
                                issue={issue} 
                                field="severity" 
                                machineId={machineId} 
                                isEditable={isManualIssue}
                              />
                            </td>
                            <td style={{ 
                              padding: '8px', 
                              borderBottom: '1px solid #dee2e6', 
                              fontSize: '12px',
                              verticalAlign: 'middle',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              <EditableIssueCell 
                                issue={issue} 
                                field="cause" 
                                machineId={machineId} 
                                isEditable={isManualIssue}
                              />
                            </td>
                            <td style={{ 
                              padding: '8px', 
                              borderBottom: '1px solid #dee2e6', 
                              fontSize: '12px', 
                              verticalAlign: 'middle'
                            }}>
                              <EditableIssueCell 
                                issue={issue} 
                                field="sensor" 
                                machineId={machineId} 
                                isEditable={isManualIssue}
                              />
                            </td>
                            <td style={{ 
                              padding: '8px', 
                              borderBottom: '1px solid #dee2e6', 
                              fontSize: '12px', 
                              verticalAlign: 'middle'
                            }}>
                              <EditableIssueCell 
                                issue={issue} 
                                field="resolvedDate" 
                                machineId={machineId} 
                                isEditable={isManualIssue}
                              />
                            </td>
                            <td style={{ 
                              padding: '8px', 
                              borderBottom: '1px solid #dee2e6', 
                              fontSize: '12px', 
                              verticalAlign: 'middle'
                            }}>
                              <EditableIssueCell 
                                issue={issue} 
                                field="resolvedBy" 
                                machineId={machineId} 
                                isEditable={isManualIssue}
                              />
                            </td>
                            <td style={{ 
                              padding: '8px', 
                              borderBottom: '1px solid #dee2e6',
                              fontSize: '12px',
                              textAlign: 'center',
                              verticalAlign: 'middle'
                            }}>
                              <a 
                                href={issue.protocolLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                  color: '#007aff', 
                                  textDecoration: 'none',
                                  fontSize: '11px'
                                }}
                              >
                                📄 Link
                              </a>
                            </td>
                            {isAdmin && (
                              <td style={{ 
                                padding: '8px', 
                                borderBottom: '1px solid #dee2e6',
                                fontSize: '12px',
                                textAlign: 'center',
                                verticalAlign: 'middle'
                              }}>
                                {isManualIssue && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteIssue(machineId, issue.id);
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#dc3545',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      padding: '4px',
                                      borderRadius: '3px'
                                    }}
                                    title="Delete issue"
                                  >
                                    ✕
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  // Special editable cell for 'typ' field - editable by admins AND jonas@oater.de
  const SpecialEditableTypCell = ({ machineId, value }: {
    machineId: string;
    value: string;
  }) => {
    const field = 'typ';
    const cellKey = `${machineId}-${field}`;
    const isEditing = editingCell === cellKey;

    // Allow editing if user is admin OR jonas@oater.de
    const canEdit = isAdmin || currentUser?.email === 'jonas@oater.de';

    if (!canEdit) {
      return (
        <span style={{
          padding: '4px 8px',
          display: 'block',
          minHeight: '16px',
          fontSize: '12px'
        }}>
          {value}
        </span>
      );
    }

    const updateConfig = async (newValue: string) => {
      try {
        await axios.put(CONFIG_API_URL, {
          machineId,
          field,
          value: newValue
        });

        // Refresh config data
        await fetchConfigData();
        setEditingCell(null);
      } catch (error) {
        console.error('Error updating config:', error);
        setEditingCell(null);
      }
    };

    if (isEditing) {
      return (
        <select
          value={value === 'Not Set' ? '' : value}
          onChange={(e) => {
            const newValue = e.target.value || 'Not Set';
            updateConfig(newValue);
            setEditingCell(null);
          }}
          onBlur={() => setEditingCell(null)}
          autoFocus
          style={{
            border: '2px solid #007aff',
            borderRadius: '4px',
            padding: '4px 8px',
            width: '100%',
            fontSize: '12px'
          }}
        >
          <option value="">Not Set</option>
          <option value="Kauf/Leasing">Kauf/Leasing</option>
          <option value="PPU">PPU</option>
          <option value="Pilot">Pilot</option>
        </select>
      );
    }

    return (
      <span
        onClick={() => setEditingCell(cellKey)}
        style={{
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: '4px',
          display: 'block',
          minHeight: '16px',
          backgroundColor: value === 'Not Set' ? '#f8f9fa' : 'transparent',
          color: value === 'Not Set' ? '#6c757d' : 'inherit',
          fontSize: '12px'
        }}
        title="Click to edit payment model"
      >
        {value}
      </span>
    );
  };

  // Editable cell component for machine configs
  const EditableConfigCell = ({ machineId, field, value }: {
    machineId: string;
    field: keyof MachineConfig;
    value: string;
  }) => {
    const cellKey = `${machineId}-${field}`;
    const isEditing = editingCell === cellKey;

    // Only allow editing if user is admin
    if (!isAdmin) {
      return (
        <span style={{ 
          padding: '4px 8px',
          display: 'block',
          minHeight: '16px',
          fontSize: '12px'
        }}>
          {value}
        </span>
      );
    }

    const updateConfig = async (newValue: string) => {
      try {
        await axios.put(CONFIG_API_URL, {
          machineId,
          field,
          value: newValue
        });
        
        // Refresh config data
        await fetchConfigData();
        setEditingCell(null);
      } catch (error) {
        console.error('Error updating config:', error);
        // Error notification - replace with toast in production
        setEditingCell(null);
      }
    };

    if (isEditing) {
      return (
        <input
          type="text"
          defaultValue={value === 'Not Set' ? '' : value}
          onBlur={(e) => updateConfig(e.target.value || 'Not Set')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              updateConfig((e.target as HTMLInputElement).value || 'Not Set');
            }
            if (e.key === 'Escape') {
              setEditingCell(null);
            }
          }}
          autoFocus
          style={{
            border: '2px solid #007aff',
            borderRadius: '4px',
            padding: '4px 8px',
            width: '100%',
            fontSize: '12px'
          }}
          placeholder={`Enter ${field}...`}
        />
      );
    }

    return (
      <span 
        onClick={() => setEditingCell(cellKey)}
        style={{ 
          cursor: 'pointer', 
          padding: '4px 8px',
          borderRadius: '4px',
          display: 'block',
          minHeight: '16px',
          backgroundColor: value === 'Not Set' ? '#f8f9fa' : 'transparent',
          color: value === 'Not Set' ? '#6c757d' : 'inherit',
          fontSize: '12px'
        }}
        title={`Click to edit ${field}`}
      >
        {value}
      </span>
    );
  };

  useEffect(() => {
    // Load configs first
    fetchConfigData();
    // Load machine errors for issue tracking
    fetchMachineErrors();
  }, []);
  
  // Will setup last log fetching after fetchLastLogData is declared

  // Config data loads automatically - no need to fetch additional data
  // Last Log timestamps now come from pre-calculated config.counters.last_log

  // useEffect(() => {
  //   if (data?.events) {
  //     //const stats = calculateProductionStats(data.events);
  //     const stats = calculateProductionStats(data.events, (data as any).machineErrors);
  //     setMachineStats(stats);
  //   }
  // }, [data, selectedMonth, selectedYear]);
  // Machine stats now come from optimized counter data, no need for heavy event processing
  useEffect(() => {
    if (configData?.configs) {
      // Create minimal stats object from counter data
      const stats: Record<string, MachineStats> = {};
      Object.entries(configData.configs).forEach(([hwId, config]) => {
        stats[hwId] = {
          today: config.counters?.today || 0,
          thisWeek: config.counters?.this_week || 0,
          selectedMonth: getSelectedMonthCount(config),
          total: config.counters?.customer_total || 0,
          lifetimeTotal: config.counters?.lifetime_total || 0,
          lastActivity: config.counters?.last_activity || null,
          errorInfo: {
            lastErrorTime: null,
            errorCount: 0,
            warningCount: 0,
            status: 'OK' as const,
            recentErrors: []
          }
        };
      });
      setMachineStats(stats);
    }
  }, [configData, selectedMonth, selectedYear, getSelectedMonthCount]);

  // Fetch real-time last log data efficiently from TimeStream
  const fetchLastLogData = useCallback(async (configToUse?: ConfigData) => {
    try {
      console.log('🔥 Fetching real-time last log timestamps from TimeStream');
      
      const config = configToUse || configData;
      if (!config) {
        console.log('⏳ No config data yet');
        return;
      }
      
      // Get all SW IDs from config to query TimeStream
      const swIds = Object.values(config.configs)
        .map(machineConfig => machineConfig.hw_id)
        .filter(id => id && id !== 'Not Set');
      
      if (swIds.length === 0) {
        console.log('⚠️ No SW IDs configured');
        return;
      }
      
      console.log('🔍 Querying TimeStream for SW IDs:', swIds);
      
      // Query the efficient last log API
      const response = await axios.get(`${LAST_LOG_API_URL}?machineIds=${swIds.join(',')}`, { 
        timeout: 10000 
      });
      
      const parsedData = typeof response.data.body === 'string' 
        ? JSON.parse(response.data.body) 
        : response.data;
      
      console.log('📊 TimeStream last log response:', {
        totalMachines: parsedData.totalMachines,
        sampleLogs: Object.keys(parsedData.lastLogs || {}).slice(0, 3)
      });
      
      // Create mapping from SW ID (from TimeStream) to HW ID (for display)
      const swIdToHwId: Record<string, string> = {};
      Object.entries(config.configs).forEach(([hwId, machineConfig]) => {
        if (machineConfig.hw_id && machineConfig.hw_id !== 'Not Set') {
          swIdToHwId[machineConfig.hw_id] = hwId;
        }
      });
      
      // Map TimeStream results (SW ID based) to HW ID based for display
      const lastLogs: Record<string, string | null> = {};
      Object.entries(parsedData.lastLogs || {}).forEach(([swId, timestamp]) => {
        const hwId = swIdToHwId[swId];
        if (hwId && timestamp) {
          lastLogs[hwId] = timestamp as string;
          console.log(`📝 Mapped ${swId} -> ${hwId}: ${timestamp}`);
        }
      });
      
      console.log('✅ Final last logs:', lastLogs);
      setLastLogTimestamps(lastLogs);
      
    } catch (err) {
      console.error('❌ Failed to fetch last log data from TimeStream:', err);
    }
  }, []); // Remove configData dependency to prevent infinite loop

  const fetchMachineErrors = async () => {
    try {
      console.log('Fetching machine errors from API');
      const response = await axios.get(MACHINE_ERRORS_API_URL);
      const parsedData = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data;

      console.log('Machine errors response:', parsedData);

      // Transform API data to issue tracking format
      const machineErrors = parsedData.machineErrors || {};
      const newIssueTracking: Record<string, Array<{
        id: string,
        date: string,
        errorId: string,
        severity: string,
        cause: string,
        sensor: string,
        resolvedDate: string,
        resolvedBy: string,
        protocolLink: string
      }>> = {};

      Object.entries(machineErrors).forEach(([machineId, errors]: [string, any]) => {
        if (Array.isArray(errors)) {
          newIssueTracking[machineId] = errors.map((error: any) => ({
            id: error.id,
            date: error.time,
            errorId: error.errorId,
            severity: error.severity,
            cause: error.cause,
            sensor: error.sensor,
            resolvedDate: '',
            resolvedBy: '',
            protocolLink: `${process.env.REACT_APP_SHAREPOINT_BASE_URL}/${machineId}/${error.id}`
          }));
        }
      });

      console.log('Transformed issue tracking data:', newIssueTracking);
      setIssueTracking(newIssueTracking);

    } catch (err) {
      console.error('Failed to fetch machine errors:', err);
    }
  };

  const fetchConfigData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(CONFIG_API_URL);
      const parsedData = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data;
      console.log('Config data received:', parsedData);
      console.log('Total machines in response:', parsedData?.totalMachines);

      // Debug specific machines that should have counter data
      ['E31', 'E13', 'E33'].forEach(machineId => {
        const machine = parsedData?.configs?.[machineId];
        if (machine) {
          console.log(`${machineId} config:`, {
            hasCounters: !!machine.counters,
            counters: machine.counters,
            lastUpdated: machine.last_updated
          });
        } else {
          console.log(`${machineId} not found in config response`);
        }
      });

      // Fetch software versions from S3 for all machines
      if (parsedData?.configs) {
        const machineIds = Object.keys(parsedData.configs);
        const versionPromises = machineIds.map(async (machineId) => {
          try {
            const version = await fetchMachineVersionFromS3(machineId);
            return { machineId, version };
          } catch (error) {
            console.error(`Failed to fetch version for ${machineId}:`, error);
            return { machineId, version: null };
          }
        });

        const versionResults = await Promise.all(versionPromises);

        // Update config data with S3 versions
        versionResults.forEach(({ machineId, version }) => {
          if (version && parsedData.configs[machineId]) {
            parsedData.configs[machineId].software_version = version;
          }
        });
      }

      setConfigData(parsedData);
      setError(null);
    } catch (err) {
      console.error('Config fetch error:', err);
      setError('Failed to fetch machine configurations');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setRefreshLoading(true);
      setError(null);

      // First, trigger counter updater lambda to recalculate counters
      console.log('Triggering counter updater...');
      try {
        await axios.get(COUNTER_UPDATER_API_URL);
        console.log('Counter updater completed');
      } catch (counterError) {
        console.warn('Counter updater failed, continuing with data load:', counterError);
        // Continue even if counter update fails
      }

      // Load config data (with fresh counters)
      await fetchConfigData();

      // Load machine errors for issue tracking
      await fetchMachineErrors();

      // fetchLastLogData will be called automatically by useEffect

    } catch (err) {
      console.error('Failed to refresh data:', err);
      setError('Failed to refresh dashboard data');
    } finally {
      setRefreshLoading(false);
    }
  };

  // Fetch last log data when configData is available
  const hasLoadedLastLogs = useRef(false);
  
  useEffect(() => {
    if (configData?.configs && !hasLoadedLastLogs.current) {
      console.log('✅ Config data loaded, now fetching TimeStream last log data');
      hasLoadedLastLogs.current = true;
      fetchLastLogData(configData); // Pass configData as parameter
    }
  }, [configData?.totalMachines]); // Only depend on stable property

  // If not logged in, show login
  if (!currentUser) {
    return <Login />;
  }

  if (loading && !data && !configData) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src={logo}
            alt="Oater Logo"
            style={{ height: '40px', width: 'auto' }}
          />
          Oater Production Dashboard
        </h1>
        <div className="stats-summary">
          <span className="stat-card">{data?.totalEvents || 0} Events</span>
          <span className="stat-card">Updated: {data ? new Date(data.lastUpdated).toLocaleTimeString() : 'Never'}</span>
          <span className="stat-card">User: {currentUser.email} {isAdmin && '(Admin)'}</span>
        </div>

        <div className="button-row">
          <button 
            onClick={refreshData} 
            className="refresh-btn"
            disabled={loading || refreshLoading}
          >
            {refreshLoading ? 'Updating counters...' : (loading ? 'Loading...' : 'Refresh')}
          </button>
          <button onClick={logout} className="refresh-btn" style={{ marginLeft: '10px' }}>Logout</button>  
        </div>
        
      </header>
      
      <div className="summary-boxes-container">
        <div className="summary-box">
          <div className="cycle-summary">
            <h3>Total Cycles in selected Month</h3>
            <div className="cycle-count">{getTotalLiveCyclesThisMonth()}</div>
            <div className="cycle-subtitle">
              Only Live machines
            </div>
          </div>
        </div>

        <div className="summary-box">
          <div className="cycle-summary">
            <h3>Machines at Customers</h3>
            <div className="cycle-count live-color">{getActiveStatusCounts().live}</div>
            <div className="cycle-subtitle">
              Live and operational
            </div>
          </div>
        </div>

        <div className="summary-box">
          <div className="cycle-summary">
            <h3>Machines Ready to Ship</h3>
            <div className="cycle-count ready-color">{getActiveStatusCounts().ready}</div>
            <div className="cycle-subtitle">
              Testing completed
            </div>
          </div>
        </div>

        <div className="summary-box">
          <div className="cycle-summary">
            <h3>Machines in Testing</h3>
            <div className="cycle-count test-color">{getActiveStatusCounts().test}</div>
            <div className="cycle-subtitle">
              Currently being tested
            </div>
          </div>
        </div>
      </div>

       <div className="machines-table">
        <table>
          <thead>
            <tr>
              <th rowSpan={2} className="machine-header">Machine</th>
              <th rowSpan={2} className="config-header hw-id-header">SW ID</th>
              <th rowSpan={2} className="config-header">Customer</th>
              <th rowSpan={2} className="config-header">City</th>
              <th className="period-header today-header">Today</th>
              <th className="period-header week-header">Week</th>
              <th className="period-header month-header">Month</th>
              <th className="period-header total-header">Total</th>
              <th className="period-header error-header">Status</th>
              <th rowSpan={2} className="config-header last-log-header">Last Log</th>
              <th rowSpan={2} className="config-header">Live Since</th>
              <th rowSpan={2} className="config-header">Payment Model</th>
              <th rowSpan={2} className="config-header">Subscription Size</th>
              <th rowSpan={2} className="config-header">Service Package</th>
              <th rowSpan={2} className="config-header expand-header">{isAdmin ? 'Service' : 'Details'}</th>
            </tr>
            <tr>
              <th className="timeframe-label">{new Date().toLocaleDateString('en-US', { day: 'numeric' })}</th>
              <th className="timeframe-label">{getWeekRange().split(' - ')[0]}</th>
              <th className="timeframe-label">
                <select 
                  value={`${selectedYear}-${selectedMonth}`}
                  onChange={(e) => {
                    const [year, month] = e.target.value.split('-');
                    setSelectedYear(parseInt(year));
                    setSelectedMonth(parseInt(month));
                  }}
                  className="month-dropdown"
                >
                  {getAvailableMonths().map(({ year, month, label }) => (
                    <option key={`${year}-${month}`} value={`${year}-${month}`}>
                      {label.split(' ')[0]}
                    </option>
                  ))}
                </select>
              </th>
              <th className="timeframe-label">All</th>
              <th className="timeframe-label">Status</th>
            </tr>
          </thead>
          <tbody>
            {allMachineIds.map(machineId => { // Removed .slice(0, 20)
              const config = configData?.configs[machineId] || {
                location: 'Not Set', // This is now treated as customer data
                city: 'Not Set', // New city field
                hw_id: 'Not Set',
                subscription_size: 'Not Set',
                service_package: 'Not Set',
                live_since: 'Not Set',
                typ: 'Not Set',
                ip_address: 'Not Set',
                ip_office: 'Not Set',
                softwarre_version: 'Not Set',
                sim_card: 'Not Set',
                data_active: 'Not Set',
                data_active_until: 'Not Set',
                software_version: 'Not Set',
                comments: '',
                last_updated: null,
                counters: {
                  today: 0,
                  this_week: 0,
                  current_month: 0,
                  customer_total: 0,
                  lifetime_total: 0,
                  last_activity: null,
                  last_counter_update: null,
                  last_processed_timestamp: '1970-01-01T00:00:00.000Z'
                }
              };
              
              const stats = {
                today: config.counters?.today || 0,
                thisWeek: config.counters?.this_week || 0,
                selectedMonth: getSelectedMonthCount(config),
                total: config.counters?.customer_total || 0,
                lastActivity: config.counters?.last_activity || null,
                lifetimeTotal: config.counters?.lifetime_total || 0,
                errorInfo: machineStats[machineId]?.errorInfo || {
                  lastErrorTime: null,
                  errorCount: 0,
                  warningCount: 0,
                  status: 'OK' as const,
                  recentErrors: []
                }
              };
              // Only highlight LIVE machines with data
              const isLiveMachine = getMachineStatus(config.location || '') === 'LIVE';
              const hasData = isLiveMachine && stats.total > 0;
              const isExpanded = expandedRows.has(machineId);

               return (
                <React.Fragment key={machineId}>
                  <tr key={machineId} className={hasData ? 'has-data' : 'no-data'}>
                    <td className="machine-id">{machineId}</td>
                    <td className="config-cell hw-id-cell">
                      <EditableConfigCell machineId={machineId} field="hw_id" value={config.hw_id} />
                    </td>
                    <td className={`config-cell customer-cell ${getMachineStatus(config.location).toLowerCase()}-status`}>
                      <EditableConfigCell machineId={machineId} field="location" value={config.location} />
                    </td>
                    <td className="config-cell city-cell">
                      <EditableConfigCell machineId={machineId} field="city" value={config.city} />
                    </td>
                    <td className="number today-cell">{stats.today}</td>
                    <td className="number week-cell">{stats.thisWeek}</td>
                    <td className="number month-cell">{stats.selectedMonth}</td>
                    <td className="number total-cell">{stats.total}</td>
                    <td className="error-status-cell">
                      <span style={{ color: '#28a745', fontSize: '12px' }}>●</span>
                    </td>
                    <td className="config-cell last-log-cell">
                      {lastLogTimestamps[machineId] ? 
                        (lastLogTimestamps[machineId] === 'NO_DATA' ? 
                          'Never' : 
                          new Date(lastLogTimestamps[machineId]!).toLocaleDateString()
                        ) : 
                        '-'}
                    </td>
                    <td className="config-cell">
                      <EditableConfigCell machineId={machineId} field="live_since" value={config.live_since} />
                    </td>
                    <td className="config-cell">
                      <SpecialEditableTypCell machineId={machineId} value={config.typ} />
                    </td>
                    <td className="config-cell">
                      <EditableConfigCell machineId={machineId} field="subscription_size" value={config.subscription_size} />
                    </td>
                    <td className="config-cell">
                      <EditableConfigCell machineId={machineId} field="service_package" value={config.service_package} />
                    </td>
                    <td className="expand-cell" style={{ textAlign: 'center', cursor: isAdmin ? 'pointer' : 'default' }} onClick={isAdmin ? () => toggleRowExpansion(machineId) : undefined}>
                      <span style={{ fontSize: '14px', color: isAdmin ? '#666' : '#ccc' }}>
                        {isAdmin ? (expandedRows.has(machineId) ? '▼' : '▶') : '—'}
                      </span>
                    </td>
                  </tr>
                  {isAdmin && isExpanded && <ExpandedMachineDetails errorInfo={stats.errorInfo} machineId={machineId} config={config} stats={stats} />}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default Dashboard;